<template lang="pug">
  .right-sidebar(:class='{ collapsed: rightSideNavCollapsed && screenLessThan1100 || !screenLessThan1100 }')
    .company
      company-switcher(v-if='showCompanySwitcher')
    .apps
      .app.special(v-if='isSuperUser' key='superuser' @click='navigateToSuperadmin' :class='{ active: isSuperAdminAppActive }')
        .app-name Super Admin

      .app.special(v-if='showCompanyAdminApp' key='companyAdmin' @click='navigateToCompanyAdmin' :class='{ active: isCompanyAdminAppActive }')
        .app-name Company Admin

    .app-list
      .app(v-if='activeCompany' v-for='app in allowedAppList' :key='app.codename' :class='appClass(app.codename)' @click='navigateToApp(app.codename, app.routeName)')
        .app-icon
          icon(:name='app.icon')
        .app-name {{ app.label }}

    .user(slot="reference")
      icon(name="user" scale="1.5")
      .user-name {{ user.firstName }} {{ user.lastName }}
      .email {{ user.email }}

      el-tag.danger(v-if='isSuperUser' type='danger') SuperAdmin

      .groups
        el-tag.group(v-for='group in assignedGroups' :key='group.id' type='gray') {{ group.name }}


      .user-action
        router-link(:to='{ name: "userAccount" }' @click.native="closeRightSidebar")
          el-button(type="primary") Edit your account
      .user-action
        el-button(type="warning" @click="logout" :loading="logoutLoading") Sign out

</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import chroma from "chroma-js";
import thumbUrl from "@/shared/utils/thumbUrl";

import UserPopup from "@/components/layout/UserPopup";
import CompanySwitcher from "@/components/layout/CompanySwitcher";

export default {
  components: { UserPopup, CompanySwitcher },
  data() {
    return {
      logoutLoading: false,
      alwaysAllowedApps: ["spage"],
      apps: [
        {
          codename: "spage",
          icon: "bullhorn",
          label: "sPage",
          routeName: "panelList"
        },
        {
          codename: "brapp",
          icon: "sliders-h",
          label: "BrApp",
          routeName: "dashboard"
        },
        {
          codename: "insight",
          icon: "lightbulb",
          label: "Insight",
          routeName: "dashboard"
        },
        {
          codename: "itrain",
          icon: "star",
          label: "iTrain",
          routeName: "dashboard"
        }
      ]
    };
  },
  computed: {
    ...mapGetters("auth", [
      "user",
      "assignedGroups",
      "isSuperUser",
      "allowedApps",
      "isActiveCompanyAdmin",
      "activeCompany"
    ]),
    ...mapGetters("ui", [
      "colorScheme",
      "sidenavCollapsed",
      "rightSideNavCollapsed"
    ]),
    showCompanyAdminApp() {
      return (
        this.activeCompany && (this.isSuperUser || this.isActiveCompanyAdmin)
      );
    },
    isCompanyAdminAppActive() {
      return this.$route.path.startsWith("/company-admin");
    },
    isSuperAdminAppActive() {
      return this.$route.path.startsWith("/super-admin");
    },
    allowedAppList() {
      if (this.allowedApps === "*") {
        return this.apps;
      }
      if (!this.allowedApps.length) {
        return [];
      }
      return this.apps.filter(app => {
        return (
          this.alwaysAllowedApps.includes(app.codename) ||
          !!this.allowedApps.find(a => a.codename === app.codename)
        );
      });
    },
    showCompanySwitcher() {
      return this.user && this.isSuperUser;
    },
    barStyle() {
      const hasCompanyLogo = this.activeCompany && this.activeCompany.logoUrl;

      return {
        backgroundColor: this.colorScheme.primaryColour,
        backgroundImage: hasCompanyLogo
          ? `url(${thumbUrl(this.activeCompany.logoUrl, 200, 50)})`
          : undefined,
        backgroundSize: hasCompanyLogo ? "auto" : undefined
      };
    },
    logoStyle() {
      return {
        backgroundColor: chroma(this.colorScheme.secondaryColour)
          .darken(0.5)
          .hex()
      };
    },
    screenLessThan1100() {
      return this.$mq.resize && this.$mq.below(1100);
    }
  },
  methods: {
    ...mapMutations("ui", ["toggleSidenav", "toggleRightSidenav"]),
    ...mapMutations({
      setActiveCompany: "auth/setActiveCompany"
    }),
    appClass(appName) {
      return {
        active: this.$route.params.app === appName
      };
    },
    navigateToApp(appCodename, name = "dashboard") {
      const routeName = `${appCodename}:${name}`;
      this.$router.push({ name: routeName, params: { app: appCodename } });
    },
    navigateToSuperadmin() {
      this.setActiveCompany({ company: null });
      this.$router.push({ name: "superAdmin:companyList" });
      this.closeRightSidebar();
    },
    navigateToCompanyAdmin() {
      this.$router.push({ name: "companyAdmin:dashboard" });
      this.closeRightSidebar();
    },
    logout() {
      this.logoutLoading = true;
      this.$router.push({ name: "login" });
      this.closeRightSidebar();
      this.$store.dispatch("auth/logout").then(() => {
        this.logoutLoading = false;
        // window.Intercom('shutdown')
      });
    },
    closeRightSidebar() {
      if (this.screenLessThan1100) {
        this.toggleRightSidenav();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/mixins.scss";

.right-sidebar {
  width: 240px;
  min-width: 240px;
  flex-basis: 240px;
  transition: all 250ms ease-in-out;
  background: #4a4558;
  color: white;
  overflow-y: auto;
  height: 100%;
  opacity: 1;

  &.collapsed {
    opacity: 0;
    width: 0px;
    min-width: 0px;
    flex-basis: 0px;
    .user {
      display: none;
    }
  }
  .company {
    padding: 10px;
    border-bottom: 1px solid white;
  }
  .apps {
    .app {
      border-bottom: 1px solid white;
      padding: 20px 10px;
      .app-name {
        font-family: "gilroyextrabold";
        font-weight: bold;
        font-size: 14px;
      }
    }
  }
  .app-list {
    .app {
      display: flex;
      padding: 20px 10px;
      justify-content: flex-start;
      align-items: center;
      border-bottom: 1px solid white;
      font-family: "gilroyextrabold";
      font-weight: bold;
      font-size: 14px;
      .app-icon {
        width: 20px;
        margin-right: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
  .user {
    display: flex;
    padding: 10px;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid white;
    .user-name,
    .email {
      font-family: "gilroyextrabold";
      font-weight: bold;
      font-size: 12px;
      margin: 10px 0;
    }
    .user-action {
      margin-bottom: 10px;
      button {
        width: 200px;
      }
    }
    .danger {
      margin-bottom: 10px;
    }
  }
}
</style>
